import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Descriptions, Form, Input, Modal, Popconfirm, Select, Spin, Table, Tooltip, Upload, message } from 'antd';
import moment from 'moment';
import {
  OPS_MAX_UPLOAD_BYTES, createOpsTicket, describeOpsError, getOpsTicket, listOpsComplaintTypes,
  listOpsTicketsForAsset, readOpsToken, uploadOpsAttachment,
} from '../../util/opsApi';
import OpsTicketDetail, { EMPTY, formatDate, StatusTag } from './OpsTicketDetail';

/**
 * "IOT Ticket" button on the Asset History Report — raises an IoT ticket in the Ops
 * module for this asset, without an AssetPro job card. See docs/ops-iot-tickets.md.
 *
 * Shown next to Live / BLE Live, under the same condition (QuickActionBar).
 *
 * On open (once per open of the modal):
 *  - GET /setting/organisation/:id (AssetPro) for the Ops token
 *  - GET {Ops}/integration/support-tickets?assetCode=<code>&state=open — so the user
 *    sees what is already raised before raising another
 *  - GET {Ops}/integration/support-tickets/complaint-types — issue picker
 * On submit: POST {Ops}/integration/support-tickets, then one POST per attachment.
 *
 * Prefilled asset fields are sent exactly as the report shows them; a field the
 * asset does not have is sent as absent, never guessed.
 */

export const OPS_TICKETS_CHANGED_EVENT = 'assetpro:ops-tickets-changed';

const orNull = (v) => (v === null || v === undefined || String(v).trim() === '' ? undefined : v);

/** Asset fields from the report payload, as the report displays them. */
const assetSnapshot = (data, devices) => {
  const asset = (data && data.asset) || {};
  const sub = asset.equipmentSubType || {};
  const tag = devices.taggedDeviceAsset;
  const ble = devices.bleTagAsset;
  return {
    assetCode: orNull(asset.assetCode),
    ownerCode: orNull(asset.ownerCode),
    assetType: orNull(sub.assetType && sub.assetType.name),
    assetSubType: orNull(sub.name),
    location: orNull(asset.location && asset.location.name),
    subLocation: orNull(asset.client && asset.client.name), // "Present Sub Location" on the report
    deviceImei: orNull(tag && tag.tagDeviceId),
    bleTagId: orNull(ble && ble.bleTagId),
  };
};

function RaiseTicketForm({ form, onForm, snapshot, complaintTypes, fileList, setFileList }) {
  const { getFieldDecorator } = form;
  // Hands the rc-form instance to the parent, whose modal footer submits it.
  // The parent keeps it in a ref: rc-form passes a new `form` object on every
  // render, so storing it in state would re-render forever.
  useEffect(() => { onForm(form); });
  const byCategory = complaintTypes.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <Form layout="vertical">
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Asset Code">{snapshot.assetCode || EMPTY}</Descriptions.Item>
        <Descriptions.Item label="Owner Code">{snapshot.ownerCode || EMPTY}</Descriptions.Item>
        <Descriptions.Item label="Asset Type">{[snapshot.assetType, snapshot.assetSubType].filter(Boolean).join(' / ') || EMPTY}</Descriptions.Item>
        <Descriptions.Item label="Location">{[snapshot.location, snapshot.subLocation].filter(Boolean).join(' / ') || EMPTY}</Descriptions.Item>
        {/* Device rows are for reference only (not sent to Ops) and appear only when the asset has that device. */}
        {snapshot.deviceImei && <Descriptions.Item label="Device (IMEI)">{snapshot.deviceImei}</Descriptions.Item>}
        {snapshot.bleTagId && <Descriptions.Item label="BLE Tag">{snapshot.bleTagId}</Descriptions.Item>}
        <Descriptions.Item label="Complaint Date" span={2}>{moment().format('DD MMM YYYY HH:mm')} (now)</Descriptions.Item>
      </Descriptions>

      <Form.Item label="Issue type" extra="From the Ops troubleshooting guide. Leave empty if unsure — the Ops team will classify it.">
        {getFieldDecorator('complaintTypeCode')(
          <Select allowClear showSearch optionFilterProp="children" placeholder="Select the closest issue (optional)">
            {Object.keys(byCategory).sort().map(category => (
              <Select.OptGroup key={category} label={category}>
                {byCategory[category].map(t => (
                  <Select.Option key={t.code} value={t.code}>{`${t.code}. ${t.issue}`}</Select.Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>,
        )}
      </Form.Item>

      <Form.Item label="What is the problem?">
        {getFieldDecorator('detailsOfComplaint', {
          rules: [
            { required: true, whitespace: true, message: 'Describe the problem' },
            { max: 2000, message: 'Keep it under 2000 characters' },
          ],
        })(<Input.TextArea rows={4} placeholder="e.g. Fuel level showing 0 since yesterday although the tank was refilled" />)}
      </Form.Item>

      <Form.Item label="Cause (if known)">
        {getFieldDecorator('causeOfDamage', { rules: [{ max: 500, message: 'Keep it under 500 characters' }] })(
          <Input placeholder="Optional" />,
        )}
      </Form.Item>

      <Form.Item label="Photos / documents" extra="Optional. Up to 15 MB each. Uploaded to the ticket right after it is created.">
        <Upload
          multiple
          fileList={fileList}
          beforeUpload={(file) => {
            if (file.size > OPS_MAX_UPLOAD_BYTES) {
              message.error(`${file.name} is larger than 15 MB`);
              return false;
            }
            setFileList(prev => [...prev, file]);
            return false; // uploaded after the ticket exists
          }}
          onRemove={(file) => setFileList(prev => prev.filter(f => f.uid !== file.uid))}
        >
          <Button icon="paper-clip">Add files</Button>
        </Upload>
      </Form.Item>
    </Form>
  );
}

const WrappedRaiseTicketForm = Form.create()(RaiseTicketForm);

export default function RaiseTicketButton({ data, authUser, devices }) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [token, setToken] = useState(null);
  const [openTickets, setOpenTickets] = useState(null);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [typesError, setTypesError] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { ticketNo, failedFiles: [] }
  const [viewTicket, setViewTicket] = useState(null); // { id, detail }
  const formRef = useRef(null);

  const snapshot = assetSnapshot(data, devices);

  const loadContext = async () => {
    setLoading(true);
    setLoadError(null);
    setTypesError(null);
    try {
      const orgId = authUser && authUser.organisation && authUser.organisation._id;
      const tokenResult = await readOpsToken(orgId);
      if (tokenResult.error) {
        setLoadError(tokenResult.error);
        return;
      }
      setToken(tokenResult.token);
      const [ticketsRes, typesRes] = await Promise.all([
        listOpsTicketsForAsset(tokenResult.token, snapshot.assetCode, 'open'),
        // The issue list is optional for the form, so its failure is shown but does not block raising.
        listOpsComplaintTypes(tokenResult.token).catch(e => ({ error: e })),
      ]);
      if (!Array.isArray(ticketsRes.data)) throw new Error('Ops returned an unexpected response for the ticket list.');
      setOpenTickets(ticketsRes.data);
      if (typesRes.error || !Array.isArray(typesRes.data)) {
        setTypesError(`Issue types could not be loaded: ${typesRes.error ? describeOpsError(typesRes.error) : 'unexpected response'}`);
        setComplaintTypes([]);
      } else {
        setComplaintTypes(typesRes.data);
      }
    } catch (e) {
      setLoadError(e && e.response ? describeOpsError(e) : (e && e.message) || describeOpsError(e));
    } finally {
      setLoading(false);
    }
  };

  const open = () => {
    setVisible(true);
    setResult(null);
    setViewTicket(null);
    setFileList([]);
    setOpenTickets(null);
    if (!snapshot.assetCode) {
      setLoadError('This asset has no asset code, so a ticket cannot be raised for it.');
      return;
    }
    loadContext();
  };

  const close = () => {
    if (submitting) return;
    setVisible(false);
    setToken(null);
  };

  const showTicket = async (id) => {
    setViewTicket({ id, detail: { loading: true } });
    try {
      const response = await getOpsTicket(token, id);
      setViewTicket({ id, detail: { data: response.data } });
    } catch (e) {
      setViewTicket({ id, detail: { error: describeOpsError(e) } });
    }
  };

  const submit = () => {
    if (!formRef.current) return;
    formRef.current.validateFields(async (err, values) => {
      if (err) return;
      setSubmitting(true);
      const raisedByName = (authUser && (authUser.name || authUser.email)) || undefined;
      const body = {
        detailsOfComplaint: values.detailsOfComplaint.trim(),
        causeOfDamage: orNull(values.causeOfDamage && values.causeOfDamage.trim()),
        complaintTypeCode: values.complaintTypeCode,
        complaintDate: new Date().toISOString(),
        assetCode: snapshot.assetCode,
        ownerCode: snapshot.ownerCode,
        assetType: snapshot.assetType,
        assetSubType: snapshot.assetSubType,
        location: snapshot.location,
        subLocation: snapshot.subLocation,
        raisedByName,
      };
      let ticket;
      try {
        const response = await createOpsTicket(token, body);
        ticket = response.data && response.data.ticket;
        if (!ticket || !ticket.id) throw new Error('Ops did not return the created ticket.');
      } catch (e) {
        setSubmitting(false);
        message.error(e && e.response ? describeOpsError(e) : (e && e.message) || 'Ticket could not be raised.');
        return;
      }
      // The ticket exists now; attachment failures are reported, not hidden.
      const failedFiles = [];
      for (const file of fileList) {
        try {
          await uploadOpsAttachment(token, ticket.id, file, { uploaderName: raisedByName });
        } catch (e) {
          failedFiles.push(`${file.name}: ${describeOpsError(e)}`);
        }
      }
      setSubmitting(false);
      setResult({ ticketNo: ticket.ticketNo, failedFiles });
      window.dispatchEvent(new CustomEvent(OPS_TICKETS_CHANGED_EVENT, { detail: { assetCode: snapshot.assetCode } }));
    });
  };

  const openCount = openTickets ? openTickets.length : 0;
  const submitButton = (
    <Button type="primary" loading={submitting} disabled={loading || !!loadError || !token}
      onClick={openCount > 0 ? undefined : submit}>
      Raise Ticket
    </Button>
  );

  const ticketColumns = [
    { title: 'Ticket No', dataIndex: 'ticketNo', key: 'ticketNo', render: v => <a>{v || EMPTY}</a> },
    { title: 'Details', dataIndex: 'detailsOfComplaint', key: 'details', render: v => v || EMPTY },
    { title: 'Status', dataIndex: 'status', key: 'status', render: s => <StatusTag status={s} /> },
    { title: 'Raised', dataIndex: 'complaintDate', key: 'complaintDate', render: v => formatDate(v) },
    { title: 'Last Updated', dataIndex: 'updatedAt', key: 'updatedAt', render: v => formatDate(v, true) },
  ];

  let footer = null;
  if (!result && !viewTicket) {
    footer = [
      <Button key="cancel" onClick={close} disabled={submitting}>Cancel</Button>,
      openCount > 0 ? (
        <Popconfirm key="confirm" title={`${openCount} open ticket(s) already exist for this asset. Raise another one?`}
          okText="Raise anyway" onConfirm={submit}>
          {submitButton}
        </Popconfirm>
      ) : React.cloneElement(submitButton, { key: 'submit' }),
    ];
  }

  return (
    <>
      <Tooltip placement="topLeft" title="Raise an IoT ticket with the AssetPro team for this asset">
        <Button className='gx-btn-purple' style={{ marginBottom: 0, marginRight: '3px' }} onClick={open}>
          IOT Ticket
        </Button>
      </Tooltip>
      <Modal
        title={`Raise IOT Ticket — ${snapshot.assetCode || 'asset code missing'}`}
        visible={visible}
        onCancel={close}
        width={viewTicket ? '90%' : 900}
        footer={footer}
        maskClosable={!submitting}
        destroyOnClose
      >
        {result ? (
          <div>
            <Alert type="success" showIcon message={`Ticket ${result.ticketNo || ''} raised with the AssetPro team.`}
              description="You can follow its status under IoT Tickets on this page or in Report → IoT Tickets Report." />
            {result.failedFiles.length > 0 && (
              <Alert style={{ marginTop: 12 }} type="warning" showIcon
                message="The ticket was raised, but some files were not attached"
                description={<ul style={{ margin: 0, paddingLeft: 18 }}>{result.failedFiles.map(f => <li key={f}>{f}</li>)}</ul>} />
            )}
            <div style={{ textAlign: 'right', marginTop: 16 }}><Button type="primary" onClick={close}>Done</Button></div>
          </div>
        ) : viewTicket ? (
          <OpsTicketDetail key={viewTicket.id} detail={viewTicket.detail} token={token} onBack={() => setViewTicket(null)} />
        ) : (
          <Spin spinning={loading}>
            {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 12 }} />}

            {openTickets && openCount === 0 ? (
              // Nothing to check — keep it to one line so the form stays in view.
              <div style={{ marginBottom: 12, color: '#389e0d' }}>
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#52c41a', marginRight: 8, verticalAlign: 'middle' }} />
                No open tickets for this asset.
              </div>
            ) : openTickets ? (
              <Card size="small" style={{ marginBottom: 16 }} title={`Open tickets for this asset (${openCount})`}>
                <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                  message="Check these before raising a new ticket — the same problem may already be reported." />
                <Table rowKey="id" size="small" pagination={false} columns={ticketColumns} dataSource={openTickets}
                  onRow={record => ({ onClick: () => showTicket(record.id), style: { cursor: 'pointer' } })} />
              </Card>
            ) : null}

            {typesError && <Alert type="warning" showIcon message={typesError} style={{ marginBottom: 12 }} />}
            {!loadError && (
              <WrappedRaiseTicketForm onForm={(f) => { formRef.current = f; }} snapshot={snapshot} complaintTypes={complaintTypes}
                fileList={fileList} setFileList={setFileList} />
            )}
          </Spin>
        )}
      </Modal>
    </>
  );
}
